import { STRUCTURE_TYPE } from "@user-management-context/read/domain/types/admin/teacher";
import { PERSONAL_STUDIO_LABEL } from "@academy-context/write/domain/constants/shared";
import { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";
import type { AddressLookup } from "@shared-kernel/domain/types/address-lookup";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { OnboardTeacherBody } from "@shared-kernel/application/ports/shared/shared-repository";
import { teacherPedagogyDefaultValues } from "@user-management-context/primary/teacher/teacher-pedagogy-subform/teacher-pedagogy.subform.schema";
import type { TeacherPedagogyFormInputs } from "@user-management-context/primary/teacher/teacher-pedagogy-subform/teacher-pedagogy.subform.schema";
import {
  normalizeSocialUrlForSubmit,
  normalizeWebsiteUrlForSubmit,
} from "@user-management-context/primary/teacher/teacher-public-profile-subform/teacher-public-profile.subform.schema";
import type { PublicProfileStepInputs } from "./public-profile-step.schema";
import type { StudioEnvironmentFormInputs } from "@academy-context/primary/shared/studio-environment/studio-environment.schema";
import {
  defaultStudioEnvironment,
  formatEnvironmentFormToBody,
} from "@academy-context/primary/shared/studio-environment/studio-environment.schema";
import { normalizeIban, normalizeIdentifier, normalizeSiret } from "@utils/formatting";

// ============================================================================
// Step IDs Enum
// ============================================================================

export enum ONBOARDING_STEP {
  ACTIVITY = 0,
  PEDAGOGY = 1,
  PUBLIC_PROFILE = 2,
  BILLING = 3,
  STUDIO = 4,
  RECAP = 5,
}

export const ONBOARDING_STEP_LABELS: Record<ONBOARDING_STEP, string> = {
  [ONBOARDING_STEP.ACTIVITY]: "Activité",
  [ONBOARDING_STEP.PEDAGOGY]: "Pédagogie",
  [ONBOARDING_STEP.PUBLIC_PROFILE]: "Profil public",
  [ONBOARDING_STEP.BILLING]: "Facturation",
  [ONBOARDING_STEP.STUDIO]: "Studio",
  [ONBOARDING_STEP.RECAP]: "Récap",
};

// ============================================================================
// Studio Option Enum
// ============================================================================

export enum STUDIO_OPTION {
  PARTNER = "partner",
  PERSONAL = "personal",
  OTHER = "other",
}

// ============================================================================
// Studio Details Type (used in PERSONAL / OTHER variants)
// ============================================================================

export interface StudioDetails {
  name: string;
  postalAddress: AddressLookup;
  addressComplement: string;
  country: SUPPORTED_COUNTRY;
  equipmentDescription: string;
  price: Nullable<number>;
  isMobilityAccessible: boolean;
  environment: StudioEnvironmentFormInputs;
}

// ============================================================================
// Default Values
// ============================================================================

const emptyAddress: AddressLookup = { placeId: "", description: "", sessionToken: "", invalid: false };

const publicProfileStepDefaultValues: PublicProfileStepInputs = {
  description: "",
  pedagogicalExperience: "",
  specializations: [],
  offersOnlineCoaching: false,
  websiteUrl: "",
  instagramUrl: "",
  youtubeUrl: "",
  tiktokUrl: "",
  facebookUrl: "",
  isPublicProfileVisibleByTeacher: false,
  isEmailVisibleOnPublicProfile: false,
};

export const defaultValues = {
  defaultPrice: null as Nullable<number>,
  siret: "",
  iban: "",
  activityRegistrationNumber: "",
  structureType: STRUCTURE_TYPE.AUTO_ENTREPRISE,
  businessName: "",
  postalAddress: emptyAddress,
  addressComplement: "",
  country: SUPPORTED_COUNTRY.FRANCE,
  studio: {
    option: undefined as STUDIO_OPTION | undefined,
    studioId: null as Nullable<string>,
    details: null as Nullable<StudioDetails>,
  },
  ...teacherPedagogyDefaultValues,
  ...publicProfileStepDefaultValues,
};

// ============================================================================
// Studio Details Builders
// ============================================================================

export const buildPersonalStudioDetails = (): StudioDetails => ({
  name: PERSONAL_STUDIO_LABEL,
  postalAddress: { ...emptyAddress },
  addressComplement: "",
  country: SUPPORTED_COUNTRY.FRANCE,
  equipmentDescription: "",
  price: 0,
  isMobilityAccessible: false,
  environment: defaultStudioEnvironment,
});

export const buildOtherStudioDetails = (): StudioDetails => ({
  name: "",
  postalAddress: { ...emptyAddress },
  addressComplement: "",
  country: SUPPORTED_COUNTRY.FRANCE,
  equipmentDescription: "",
  price: null,
  isMobilityAccessible: false,
  environment: defaultStudioEnvironment,
});

// ============================================================================
// Formatter Function (converts form data to API body)
// ============================================================================

const emptyToNull = (value: string): Nullable<string> => (value.trim() === "" ? null : value);

export const formatFormDataToOnboardTeacherBody = (
  data: {
    defaultPrice: Nullable<number>;
    siret: string;
    iban: string;
    activityRegistrationNumber: string;
    structureType: STRUCTURE_TYPE;
    businessName: string;
    postalAddress: AddressLookup;
    country: SUPPORTED_COUNTRY;
    addressComplement: string;
    studio: {
      option: STUDIO_OPTION | undefined;
      studioId: Nullable<string>;
      details: Nullable<StudioDetails>;
    };
  } & TeacherPedagogyFormInputs &
    PublicProfileStepInputs,
): OnboardTeacherBody => {
  if (data.defaultPrice === null) throw new Error("defaultPrice is required");

  const studio =
    data.studio.option === STUDIO_OPTION.PARTNER
      ? { studioId: data.studio.studioId ?? "" }
      : (() => {
          if (!data.studio.details) throw new Error("studio details required");
          return {
            details: {
              name: data.studio.details.name,
              postalAddress: data.studio.details.postalAddress,
              addressComplement: data.studio.details.addressComplement || null,
              equipmentDescription: data.studio.details.equipmentDescription,
              price: data.studio.details.price ?? 0,
              isMobilityAccessible: data.studio.details.isMobilityAccessible,
              environment: formatEnvironmentFormToBody(data.studio.details.environment),
            },
          };
        })();

  return {
    defaultPrice: data.defaultPrice,
    siret: normalizeSiret(data.siret, data.country),
    iban: normalizeIban(data.iban),
    activityRegistrationNumber: normalizeIdentifier(data.activityRegistrationNumber) || null,
    structureType: data.structureType,
    businessName: data.businessName || null,
    studio,
    postalAddress: data.postalAddress,
    addressComplement: data.addressComplement || null,
    teacherResumeUrl: data.teacherResumeUrl,
    artistResumeUrl: emptyToNull(data.artistResumeUrl),
    description: data.description,
    pedagogicalExperience: data.pedagogicalExperience,
    specializations: data.specializations,
    certifications: data.certifications,
    teachingMethodologies: data.teachingMethodologies,
    websiteUrl: normalizeWebsiteUrlForSubmit(data.websiteUrl),
    instagramUrl: normalizeSocialUrlForSubmit(data.instagramUrl),
    youtubeUrl: normalizeSocialUrlForSubmit(data.youtubeUrl),
    tiktokUrl: normalizeSocialUrlForSubmit(data.tiktokUrl),
    facebookUrl: normalizeSocialUrlForSubmit(data.facebookUrl),
    isPublicProfileVisibleByTeacher: data.isPublicProfileVisibleByTeacher,
    isEmailVisibleOnPublicProfile: data.isEmailVisibleOnPublicProfile,
    offersOnlineCoaching: data.offersOnlineCoaching,
  };
};
